/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { useCallback, useState } from "react";
import { CommerceInfo } from "components/EnhancedSpeech/Utilities";
import {
  isCurrentUserTokenValid,
  useCurrentUser,
} from "actions/CurrentUserActions";
import { useEventListener } from "hooks/useEventListener";
import {
  Analytics,
  AnalyticsEvent,
  ExpressCheckoutButtonPosition,
} from "constants/Analytics";
import { getSimpleLocale } from "utilities/UserSettingsUtilities";

export enum CheckoutStatus {
  CLOSED,
  LOADING,
  LOADED,
  THANK_YOU,
}

export const useCheckout = (
  position: ExpressCheckoutButtonPosition
): {
  checkoutStatus: CheckoutStatus;
  commerceInfo: CommerceInfo | null;
  openCheckout: () => Promise<void>;
  closeCheckout: () => void;
} => {
  const [checkoutStatus, setCheckoutStatus] = useState(CheckoutStatus.CLOSED);
  const [commerceInfo, setCommerceInfo] = useState<CommerceInfo | null>(null);

  const openCheckout = useCallback(async (): Promise<void> => {
    localStorage.setItem("lastCheckoutPosition", position);
    const searchParams = new URLSearchParams(window.location.search);
    const pp = searchParams.get("pp");
    const token = searchParams.get("token");

    const currentUrl = window.location.origin + window.location.pathname;
    const redirectUrl = `${currentUrl}?um=1`;

    const validCurrentUser = await isCurrentUserTokenValid();
    if (!validCurrentUser) {
      window.adobeIMS?.signIn({
        redirect_uri: redirectUrl,
      });

      return;
    }

    window.history.pushState({}, document.title, window.location.pathname);
    setCheckoutStatus(CheckoutStatus.LOADING);

    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
    document.documentElement.style.overscrollBehavior = "none";
    document.documentElement.style.overflow = "hidden";

    Analytics.instance.logEvent(
      AnalyticsEvent.EXPRESS_PREMIUM_CHECKOUT_STARTED,
      { position: position }
    );

    const profile = await window.adobeIMS?.getProfile();
    const ctxRtUrl = encodeURIComponent(redirectUrl);
    const countryCode =
      (profile?.account_type !== "guest" && profile?.account_type !== "null"
        ? profile?.countryCode
        : undefined) ?? "US";
    const offerId = "0CE62375A31494AA5908869C617C98AA"; //This is the offer ID for Adobe Podcast Premium
    const baseUrl =
      localStorage.getItem("checkoutReturnUrl") ??
      `${
        process.env.COMMERCE_IFRAME_URL
      }/store/commitment?items%5B0%5D%5Bid%5D=${offerId}&cli=adobe_podcast&co=${countryCode}&lang=${getSimpleLocale()}&ctx=if&lo=sl&th=ccx&ctxRtUrl=${ctxRtUrl}`;

    const url = `${baseUrl}${pp ? `&pp=${pp}` : ""}${
      token ? `&token=${token}` : ""
    }`;

    localStorage.removeItem("checkoutReturnUrl");

    setCommerceInfo({
      domain: process.env.COMMERCE_IFRAME_URL as string, // set on moonbeam
      url,
    });
  }, [position]);

  const closeCheckout = useCallback((): void => {
    setCheckoutStatus(CheckoutStatus.CLOSED);
    document.body.style.overflow = "unset";
    document.body.style.height = "100%";
    document.documentElement.style.overscrollBehavior = "auto";
    document.documentElement.style.overflow = "auto";
  }, []);

  // https://wiki.corp.adobe.com/display/businessservices/UCv3+Link+Creation+Guide#UCv3LinkCreationGuide-Web(iframe)
  const handleCheckoutIFrameMessage = useCallback(
    async (event) => {
      if (checkoutStatus === CheckoutStatus.CLOSED) return;
      if (event.origin !== commerceInfo?.domain) return;
      if (!event.data) return;
      const message =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      if (message.type === "System") {
        if (message.subType === "AppLoaded") {
          setCheckoutStatus(CheckoutStatus.LOADED);
        } else if (message.subType === "Close") {
          Analytics.instance.logEvent(
            AnalyticsEvent.EXPRESS_PREMIUM_CHECKOUT_CANCELLED,
            { position: position }
          );
          closeCheckout();
        } else if (message.subType === "OrderComplete") {
          setCheckoutStatus(CheckoutStatus.LOADING);
          Analytics.instance.logEvent(
            AnalyticsEvent.EXPRESS_PREMIUM_CHECKOUT_COMPLETED,
            { position: position }
          );

          setCheckoutStatus(CheckoutStatus.THANK_YOU);
        }
      }

      if (message.type === "OpenURL") {
        if (!message.data || !message.data.externalUrl) {
          return;
        }

        switch (message.subType) {
          case "RETURN_BACK":
            localStorage.setItem("checkoutReturnUrl", message.data.returnUrl);
            window.location.href = message.data.externalUrl;
            return;
          case "SWITCH": // Bad Country Code, TODO: untested
            window.location.href = message.data.externalUrl;
            return;
          case "EXTERNAL": // Accept privacy, TODO: untested
            window.open(message.data.externalUrl, "_blank")?.focus();
            return;
        }

        setCheckoutStatus(CheckoutStatus.CLOSED);
      }
    },
    [checkoutStatus, commerceInfo?.domain, position, closeCheckout]
  );

  useEventListener("message", handleCheckoutIFrameMessage, window);

  return {
    checkoutStatus,
    commerceInfo,
    openCheckout,
    closeCheckout,
  };
};
